import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { jsPDF } from "jspdf";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
	Box,
	Button,
	// Card as MuiCard,
	// CardHeader as MuiCardHeader,
	CardContent,
	Chip,
	Tab,
	Tabs,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@material-ui/core";

import Chart from "chart.js/auto";
import { Bar } from "react-chartjs-2";

// core components
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import FundsPopup from "views/Popups/Misc/FundsPopup.js";

//actions
import { runCalculations } from "../../actions/report";
import { updateFundsRequested } from "../../actions/user";

//helpers
import { formatResult, getFieldData, getGHGData, getN2OData, getWaterData, N2O } from "helpers/report.js";
import { createExcelReport } from "helpers/reportDownload/reportDownload.js";
import { FETCH_COMBINED_LOOKUPS } from "constants/api";
import SaveLoadButtons from "components/Field/SaveLoadButtons";
import { getKeyOutputsData } from "helpers/report";

const styles = {
	typo: {
		paddingLeft: "25%",
		marginBottom: "40px",
		position: "relative",
	},
	card: {
		marginLeft: "35%",
		marginTop: "0",
		width: "65%",
	},
	gridCard: {
		padding: "10px",
		width: 1175,
		fontSize: "0.875rem",
	},
	note: {
		fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
		bottom: "10px",
		color: "#c0c1c2",
		display: "block",
		fontWeight: "400",
		fontSize: "13px",
		lineHeight: "13px",
		left: "0",
		marginLeft: "20px",
		position: "absolute",
		width: "260px",
	},
	cardCategoryWhite: {
		color: "rgba(255,255,255,.62)",
		margin: "0",
		fontSize: "14px",
		marginTop: "0",
		marginBottom: "0",
	},
	cardTitleWhite: {
		color: "#FFFFFF",
		marginTop: "0px",
		minHeight: "auto",
		fontWeight: "300",
		fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
		marginBottom: "3px",
		textDecoration: "none",
	},
	alertRoot: {
		margin: "3px",
		flexBasis: "100%",
	},
	column: {
		flexBasis: "50%",
	},
	reportCardContainer: {
		justifyContent: "center",
	},
	tableHeader: {
		fontFamily: "inherit",
		fontWeight: 600,
		// fontSize: "0.875rem",
		// textAlign: "center",
	},
	renewableEnergyRow: {
		width: "200px",
	},
	semiBold: {
		fontWeight: 600,
	},
	chartCardContent: {
		height: 350,
		paddingTop: 0,
	},
	chartCardHeader: {
		paddingBottom: 0,
	},
};

const getNewReportData = () => ({
	waterReport: {
		beforeWaterUse: null,
		afterWaterUse: null,
		waterSavings: null,
		percentSavings: null,
		totalArea: null,
	},
	pumpingReport: {
		beforeGHG: null,
		afterGHG: null,
		renewableEnergy: null,
		yearlyGHGBenefits: null,
		tenYearGHGBenefits: null,
	},
	n2oReport: {
		beforeN2O: null,
		afterN2O: null,
		avgYearlyN2OBenefits: null,
		tenYearN2OBenefits: null,
	},
	overallReport: {
		yearlyGHGBenefits: null,
		convertedN2OBenefits: null,
		tenYearGHGBenefits: null,
		ghgBenefitsPerAcreYear: null,
		emissionReductionsPerFundsRequested: null,
	},
});

const useStyles = makeStyles(styles);

export default function Report2() {
	const classes = useStyles();

	const dispatch = useDispatch();
	const user = useSelector((state) => state.user);
	const [fundsPopupOpen, setFundsPopupOpen] = useState(!user.fundsRequested);
	const [reportData, setReportData] = useState(getNewReportData());
	const [lookupData, setLookupData] = useState({});
	const [currentTabIndex, setCurrentTabIndex] = useState(0);

	useEffect(() => {
		dispatch(runCalculations(user));

		fetch(FETCH_COMBINED_LOOKUPS)
			.then((response) => response.json())
			.then((results) => {
				var newLookupData = {};
				for (var lookup of results) {
					newLookupData[lookup.id] = lookup;
				}
				setLookupData(newLookupData);
			});
	}, []);

	useEffect(() => {
		updateReportTotals();
	}, [user]);

	const fields = useSelector((state) => state.user.fields);
	const pumpResults = useSelector((state) => state.user.pumpResults);

	const tabData = [
		getGHGData(reportData, pumpResults),
		getN2OData(reportData, fields),
		getWaterData(reportData, fields),
		getFieldData(fields),
	];

	function handleTabChange(event, newIndex) {
		setCurrentTabIndex(newIndex);
	}

	function handleFundsSubmit(fundsRequested) {
		setFundsPopupOpen(false);

		dispatch(updateFundsRequested(Number(fundsRequested)));
	}

	function updateReportTotals() {
		const totalArea = fields.reduce((sum, field) => sum + field.acreage, 0);
		if (totalArea > 0 && user.pumpResults) {
			const newReportData = getNewReportData();

			//compute total before and after water use in acre-inches
			//todo: we might be able to take out all the field.results ? checks now that everything's inside a user.pumpResults check
			newReportData.waterReport.beforeWaterUse = fields.reduce(
				(sum, field) => sum + (field.results ? field.results.beforeWater * field.acreage : 0),
				0
			);
			newReportData.waterReport.afterWaterUse = fields.reduce(
				(sum, field) => sum + (field.results ? field.results.afterWater * field.acreage : 0),
				0
			);

			newReportData.waterReport.waterSavings =
				newReportData.waterReport.beforeWaterUse - newReportData.waterReport.afterWaterUse;
			newReportData.waterReport.percentSavings =
				newReportData.waterReport.beforeWaterUse != 0
					? (newReportData.waterReport.waterSavings / newReportData.waterReport.beforeWaterUse) * 100
					: 0;
			newReportData.waterReport.totalArea = totalArea;

			newReportData.pumpingReport.beforeGHG = user.pumpResults.reduce((sum, pump) => sum + pump.ghgPre, 0);
			newReportData.pumpingReport.afterGHG = user.pumpResults.reduce((sum, pump) => sum + pump.ghgPost, 0);
			newReportData.pumpingReport.renewableEnergy = user.pumpResults.reduce(
				(sum, pump) => sum + pump.ghgSavingsRenewableEnergy,
				0
			);
			newReportData.pumpingReport.yearlyGHGBenefits =
				newReportData.pumpingReport.beforeGHG - newReportData.pumpingReport.afterGHG;
			newReportData.pumpingReport.tenYearGHGBenefits = newReportData.pumpingReport.yearlyGHGBenefits * 10;

			newReportData.n2oReport.beforeN2O = fields.reduce(
				(sum, field) => sum + (field.results ? field.results.beforeN2O : 0),
				0
			);
			newReportData.n2oReport.afterN2O = fields.reduce(
				(sum, field) => sum + (field.results ? field.results.afterN2O : 0),
				0
			);
			newReportData.n2oReport.avgYearlyN2OBenefits = fields.reduce(
				(sum, field) => sum + (field.results ? field.results.avgYearlyN2OBenefits : 0),
				0
			);
			newReportData.n2oReport.tenYearN2OBenefits = fields.reduce(
				(sum, field) => sum + (field.results ? field.results.tenYearN2OBenefits : 0),
				0
			);

			newReportData.overallReport.convertedN2OBenefits = newReportData.n2oReport.avgYearlyN2OBenefits; //convert lb N to tonnes CO2-equiv

			//todo: test that this works with no pumps defined
			newReportData.overallReport.yearlyGHGBenefits =
				newReportData.pumpingReport.yearlyGHGBenefits + newReportData.overallReport.convertedN2OBenefits;

			newReportData.overallReport.tenYearGHGBenefits = newReportData.overallReport.yearlyGHGBenefits * 10;

			if (!user.fundsRequested || !(user.fundsRequested > 0)) {
				// not using user.fundsRequested <= 0 because it doesn't catch NaN
				newReportData.overallReport.emissionReductionsPerFundsRequested = "N/A";
			} else {
				newReportData.overallReport.emissionReductionsPerFundsRequested = formatResult(
					newReportData.overallReport.tenYearGHGBenefits / user.fundsRequested
				);
			}

			newReportData.overallReport.ghgBenefitsPerAcreYear =
				totalArea != 0 ? newReportData.overallReport.yearlyGHGBenefits / totalArea : 0;

			setReportData(newReportData);
		}
	}

	function getReport() {
		createExcelReport(user, lookupData, reportData);
	}

	function getMap() {
		window.map.once("rendercomplete", function () {
			const mapCanvas = document.createElement("canvas");
			const size = window.map.getSize();
			mapCanvas.width = size[0];
			mapCanvas.height = size[1];
			const mapContext = mapCanvas.getContext("2d");
			Array.prototype.forEach.call(
				window.map.getViewport().querySelectorAll(".ol-layer canvas, canvas.ol-layer"),
				function (canvas) {
					if (canvas.width > 0) {
						const opacity = canvas.parentNode.style.opacity || canvas.style.opacity;
						mapContext.globalAlpha = opacity === "" ? 1 : Number(opacity);
						let matrix;
						const transform = canvas.style.transform;
						if (transform) {
							// Get the transform parameters from the style's transform matrix
							matrix = transform
								.match(/^matrix\(([^\(]*)\)$/)[1]
								.split(",")
								.map(Number);
						} else {
							matrix = [
								parseFloat(canvas.style.width) / canvas.width,
								0,
								0,
								parseFloat(canvas.style.height) / canvas.height,
								0,
								0,
							];
						}
						// Apply the transform to the export map context
						CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
						const backgroundColor = canvas.parentNode.style.backgroundColor;
						if (backgroundColor) {
							mapContext.fillStyle = backgroundColor;
							mapContext.fillRect(0, 0, canvas.width, canvas.height);
						}
						mapContext.drawImage(canvas, 0, 0);
					}
				}
			);
			mapContext.globalAlpha = 1;
			mapContext.setTransform(1, 0, 0, 1, 0, 0);

			const pdf = new jsPDF("landscape");
			pdf.addImage(mapCanvas.toDataURL("image/jpeg"), "JPEG", 50, 10, 180, 150);
			pdf.save("map.pdf");
		});
		window.map.renderSync();
	}

	function getOutputColor(value, colorCoded) {
		if (value[value.length - 1] === "%") {
			value = value.slice(0, -1);
		}

		if (colorCoded && !isNaN(+value)) {
			return value >= 0 ? "oklch(54.6% 0.245 262.881)" : "oklch(57.7% 0.245 27.325)";
		}
		return "inherit";
	}

	function getChartData() {
		const labels = ["Pre-Project", "Post-Project"];
		return [
			{
				cardTitle: "Water Use",
				precondition: reportData.waterReport,
				labels: labels,
				dataLabel: "Water Use",
				values: [
					reportData.waterReport.beforeWaterUse / reportData.waterReport.totalArea,
					reportData.waterReport.afterWaterUse / reportData.waterReport.totalArea,
				],
				color: "#9AD0F5",
				yAxisTitle: "Total water use (ac-in/ac)",
			},
			{
				cardTitle: "GHG Emissions",
				precondition: reportData.pumpingReport,
				labels: labels,
				dataLabel: "Pumping GHG Emissions",
				values: [reportData.pumpingReport.beforeGHG, reportData.pumpingReport.afterGHG],
				color: "#84AD5C",
				yAxisTitle: "Yearly emissions (tonnes CO2-eq/yr)",
			},
			{
				cardTitle: "N2O Emissions",
				precondition: reportData.n2oReport,
				labels: labels,
				dataLabel: "N2O Emissions",
				values: [reportData.n2oReport.beforeN2O, reportData.n2oReport.afterN2O],
				color: "#E1E130",
				yAxisTitle: "Total emissions (tonnes CO2-eq)",
			},
		];
	}

	function getChartOptions(yTitle) {
		return {
			maintainAspectRatio: false,
			scales: {
				y: { title: { display: true, text: yTitle, font: { size: 14 } } },
				x: {
					ticks: {
						font: { size: 14 },
					},
				},
			},
			plugins: {
				legend: {
					display: false,
				},
			},
		};
	}

	return (
		<Box sx={{ p: 2, maxHeight: "calc(100vh - 165px)", overflowY: "auto" }}>
			<FundsPopup open={fundsPopupOpen} handleSubmit={handleFundsSubmit} value={user.fundsRequested} />

			<Box
				display="flex"
				flexDirection="column"
				justifyContent="start"
				alignItems="stretch"
				style={{ gap: 32, maxWidth: "80rem", margin: "0 auto" }}
			>
				{/* Header */}
				<Box display="flex" flexDirection="column" justifyContent="start" alignItems="start" style={{ gap: 16 }}>
					<h1 style={{ marginBottom: 0 }}>SWEEP Reports</h1>
					<div style={{ display: "flex", flexDirection: "row", gap: 8 }}>
						<Chip size="small" label={`Report Generated: ${new Date().toLocaleDateString()}`}></Chip>
						<Chip size="small" variant="outlined" label={`Project Name: ${user.agopname}`}></Chip>
					</div>

					<Box display="flex" flexDirection="row" justifyContent="start" alignItems="center" style={{ gap: 16 }}>
						<Button
							color="primary"
							variant="contained"
							onClick={getReport}
							disabled={reportData.overallReport.yearlyGHGBenefits == null}
						>
							Download Report
						</Button>
						<Button color="primary" variant="contained" onClick={getMap}>
							Download Map
						</Button>
						<SaveLoadButtons saveText="Download Project File" loadText={null} />
					</Box>
				</Box>
				<GridContainer spacing={3}>
					{getChartData().map((chart, index) => (
						<GridItem xs={12} sm={6} md={4} key={index}>
							<Card>
								<CardHeader className={classes.chartCardHeader}>{chart.cardTitle}</CardHeader>
								<CardContent className={classes.chartCardContent}>
									{chart.precondition && (
										<Bar
											data={{
												labels: chart.labels,
												datasets: [
													{
														label: chart.dataLabel,
														data: chart.values,
														backgroundColor: chart.color,
														borderColor: chart.color,
														barThickness: 50,
													},
												],
											}}
											options={getChartOptions(chart.yAxisTitle)}
										/>
									)}
								</CardContent>
							</Card>
						</GridItem>
					))}
				</GridContainer>
				<Card>
					<CardHeader>SWEEP Project key outputs</CardHeader>
					<CardContent>
						<GridContainer spacing={3}>
							{reportData &&
								getKeyOutputsData(reportData, user.fundsRequested, () => setFundsPopupOpen(true)).map((item, index) => (
									<GridItem key={index} xs={12} sm={6} md={4}>
										<Typography>{item.label()}</Typography>
										<Typography
											style={{
												fontSize: "1.5rem",
												fontWeight: 550,
												color: getOutputColor(item.value, item.colorCoded),
											}}
										>
											{item.value}
										</Typography>
										<Typography color="textSecondary">{item.units}</Typography>
									</GridItem>
								))}
						</GridContainer>
						<Typography variant="caption" color="textSecondary" component={"p"} style={{ marginTop: 24 }}>
							* To be transferred to application
						</Typography>
					</CardContent>
				</Card>
				<div>
					<Tabs value={currentTabIndex} onChange={handleTabChange} centered>
						{tabData.map((tab, index) => (
							<Tab label={tab.label()} key={index} />
						))}
					</Tabs>
					{tabData.map((tab, index) => (
						<div role="tabpanel" hidden={currentTabIndex !== index} key={index}>
							<Card>
								<CardHeader>{tab.cardTitle}</CardHeader>
								<CardContent>
									<Table>
										<TableHead>
											<TableRow>
												{tab.columnHeaders.map((header, index) => (
													<TableCell key={index}>{header()}</TableCell>
												))}
											</TableRow>
										</TableHead>
										<TableBody>
											{tab.rows?.map((row, index) => (
												<TableRow key={index}>
													{row.map((cell, index) => (
														<TableCell key={index} className={index == 0 ? classes.semiBold : ""}>
															{cell}
														</TableCell>
													))}
												</TableRow>
											))}
											{tab.totalsRow && (
												<TableRow className={classes.semiBold}>
													{tab.totalsRow.map((cell, index) => (
														<TableCell key={index} className={index == 0 ? classes.semiBold : ""}>
															{cell}
														</TableCell>
													))}
												</TableRow>
											)}
										</TableBody>
									</Table>
								</CardContent>
							</Card>
						</div>
					))}
				</div>
			</Box>
		</Box>
	);
}
